import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
} from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { SetContextLink } from '@apollo/client/link/context';
import { RemoveTypenameFromVariablesLink } from '@apollo/client/link/remove-typename';
import { logger, offsetLimitPagination } from '@shared/utils';
import { Auth } from 'aws-amplify';
import { map } from 'rxjs/operators';
import { getLocale, getDomainName, getGraphQLEndpoint } from './config';

let lastRefreshTimestamp = Date.now();
const BACKOFFICE_SESSION_TTL_MS = 9 * 60 * 60 * 1000;

const refreshBackofficeSession = () => {
  lastRefreshTimestamp = Date.now();
};

const checkBackofficeSessionExpiry = () => {
  const now = Date.now();
  const timeSinceRefresh = now - lastRefreshTimestamp;

  if (timeSinceRefresh > BACKOFFICE_SESSION_TTL_MS) {
    window.location.reload();
    return true;
  }
  return false;
};

const removeTypenameFromVariablesLink = new RemoveTypenameFromVariablesLink();

const setupFocusEventListeners = () => {
  const handleVisibilityChange = () => {
    if (!document.hidden) {
      if (checkBackofficeSessionExpiry()) {
        return;
      }
      refreshBackofficeSession();
    }
  };

  const handleWindowFocus = () => {
    if (checkBackofficeSessionExpiry()) {
      return;
    }
    refreshBackofficeSession();
  };

  const handlePageShow = (event) => {
    if (!event.persisted) return;
    if (checkBackofficeSessionExpiry()) {
      return;
    }
    refreshBackofficeSession();
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('focus', handleWindowFocus);
  window.addEventListener('pageshow', handlePageShow);
};

setupFocusEventListeners();

const privateLink = new HttpLink({
  uri: getGraphQLEndpoint(),
});

// Note: In Apollo Client v4, context from hooks doesn't flow properly,
// so we read locale/domain directly from config
const authMiddleware = new SetContextLink(async (prevContext) => {
  let token;
  try {
    const session = await Auth.currentSession();
    token = session.getAccessToken().getJwtToken();
  } catch (error) {
    logger.error(error);
  }

  const headers = {
    ...(prevContext.headers || {}),
    Authorization: token ? `Bearer ${token}` : '',
    locale: getLocale(),
  };

  if (import.meta.env.MODE === 'development') {
    headers.domain = getDomainName();
  }

  return {
    ...prevContext,
    headers,
  };
});

const errorLink = new ErrorLink(
  ({ graphQLErrors, networkError, operation }) => {
    let errorMessage = `Failed GraphQL call\nQuery:\n${operation.operationName}`;

    if (graphQLErrors) {
      errorMessage = `${errorMessage}\nErrors:\n${graphQLErrors
        .map(({ message, path }) =>
          path ? `  Paths: ${path.join(',')}; ${message}` : message
        )
        .join('\n')}`;
    }

    if (networkError) {
      errorMessage = `${errorMessage}\nNetwork error: ${networkError}`;
    }
    logger.error(errorMessage);
  }
);

const responseMiddleware = new ApolloLink((operation, forward) => {
  return forward(operation).pipe(
    map((response) => {
      if (checkBackofficeSessionExpiry()) {
        return response;
      }

      refreshBackofficeSession();

      return response;
    })
  );
});

const client = new ApolloClient({
  link: ApolloLink.from([
    errorLink,
    authMiddleware,
    responseMiddleware,
    removeTypenameFromVariablesLink,
    privateLink,
  ]),
  cache: new InMemoryCache({
    typePolicies: {
      ArrayTranslation: {
        keyFields: ['languageCode'],
      },
      Course: {
        fields: {
          episodes: {
            merge(_, incoming) {
              return incoming;
            },
          },
          experts: {
            merge(_, incoming) {
              return incoming;
            },
          },
          name: { merge: (_, incoming) => incoming },
          description: { merge: (_, incoming) => incoming },
          shortDescription: { merge: (_, incoming) => incoming },
          learningPoints: { merge: (_, incoming) => incoming },
          prerequisites: { merge: (_, incoming) => incoming },
          typicalLearner: { merge: (_, incoming) => incoming },
        },
      },
      Group: {
        fields: {
          members: {
            merge(_, incoming) {
              return incoming;
            },
          },
        },
      },
      Query: {
        fields: {
          listExperts: {
            keyArgs: false,
            merge(existing, incoming, options) {
              return offsetLimitPagination(existing, incoming, options.args);
            },
          },
          listCourses: {
            keyArgs: ['filter', 'organizationId'],
            merge(existing, incoming, options) {
              return offsetLimitPagination(existing, incoming, options.args);
            },
          },
          listOrganizations: {
            keyArgs: false,
            merge(existing, incoming, options) {
              return offsetLimitPagination(existing, incoming, options.args);
            },
          },
          listInvitations: {
            keyArgs: ['organizationId'],
            merge(existing = [], incoming = []) {
              return [...existing, ...incoming];
            },
          },
          listMembers: {
            keyArgs: ['filter'],
            merge(existing, incoming, options) {
              return offsetLimitPagination(existing, incoming, options.args);
            },
          },
          listPlaylists: {
            keyArgs: ['filter'],
            merge(existing, incoming, options) {
              return offsetLimitPagination(existing, incoming, options.args);
            },
          },
          listGroups: {
            keyArgs: ['filter'],
            merge(existing = [], incoming = []) {
              return [...existing, ...incoming];
            },
          },
        },
      },
    },
  }),
});

export default client;
